import { Injectable } from '@angular/core';

import { PaulyOrderDto } from '../../infrastructure/dto/order.dto';
import { buildPurchaseEvent, hasReportedPurchase, isPaidOrder, markPurchaseReported } from '../purchase-event';
import { CookieConsentService } from './cookie-consent.service';

/**
 * Google Tag Manager for the webshop (PAUL-3460). Marketing places its own pixels inside the
 * container, so there are only two jobs here: load the container the POS names, and push a GA4
 * `purchase` once an order is paid. Everything downstream is configured in the GTM UI.
 *
 * The container id comes off the POS payload and only off the POS payload. A POS and its shop each
 * carry their own `tag_manager_container_id` and neither inherits the other's, so a POS reading null
 * has no container — do not fall back to `shop.tag_manager_container_id`.
 */
@Injectable({
  providedIn: 'root',
})
export class TagManagerService {
  private containerId: string | null = null;
  /* Set by the first `load()`, container or not; until then a paid order waits in `heldPurchases`. */
  private containerDecided = false;
  private readonly heldPurchases = new Map<string, PaulyOrderDto>();

  constructor(private readonly cookieConsentService: CookieConsentService) {}

  /**
   * Called wherever a POS payload is read: both checkout wrappers, and the payment-verification page,
   * which the payment provider returns straight into with no wrapper above it. Idempotent, so a
   * re-emitting `paramMap` cannot inject the container twice.
   */
  load(containerId: string | null | undefined): void {
    if (this.containerId) {
      return;
    }
    this.containerDecided = true;

    /* Null means no container at all — not a script tag, not an empty one, not a held purchase. */
    if (!containerId) {
      this.heldPurchases.clear();
      return;
    }
    this.containerId = containerId;

    /* Consent is given on the portal, never here, so it cannot change while the shop is open. */
    if (this.hasContainerConsent()) {
      this.injectContainer(containerId);
    }

    /* After the injection, so a held purchase lands behind the consent default and `gtm.js`. */
    const held = Array.from(this.heldPurchases.values());
    this.heldPurchases.clear();
    held.forEach((order) => this.pushPurchase(order));
  }

  /**
   * Reports a paid order exactly once. Fed every poll of the order, from both confirmation pages, so
   * the guards here are what keep it to one event per order across polling, refreshes, a second tab
   * and the round trip to the payment provider.
   */
  pushPurchase(order: PaulyOrderDto | null): void {
    if (!order || !isPaidOrder(order.status) || !this.hasContainerConsent()) {
      return;
    }

    /* A direct visit to the subscription order page polls before its wrapper has read the POS. The poll
       of an already-paid order runs once, so it is held for `load()`, not dropped. */
    if (!this.containerDecided) {
      this.heldPurchases.set(order.id, order);
      return;
    }

    if (!this.containerId || hasReportedPurchase(localStorage, order.id)) {
      return;
    }
    /* Marked before the push: a throw further down must not leave the order eligible to fire again. */
    markPurchaseReported(localStorage, order.id);

    /* Cleared first, the way GA4 documents it, so no `ecommerce` object left on the dataLayer by an
       earlier event can merge into this one. */
    this.dataLayer.push({ ecommerce: null });
    this.dataLayer.push(buildPurchaseEvent(order));
  }

  /* Analytics, the same consent Clarity loads on (PAUL-3460). Whether marketing consent alone may load
     the container is the PO's call, not this code's; Consent Mode already denies ad storage without it. */
  private hasContainerConsent(): boolean {
    return this.cookieConsentService.hasAnalyticsConsent();
  }

  private get dataLayer(): unknown[] {
    const target = window as unknown as { dataLayer?: unknown[] };
    target.dataLayer = target.dataLayer ?? [];

    return target.dataLayer;
  }

  private injectContainer(containerId: string): void {
    /* `gtm.start` goes on the queue before the script, which is what the container reads to time
       itself; anything pushed before it loads is replayed on load, so the purchase event cannot be
       lost by racing this. No `<noscript>` iframe counterpart: this is a SPA, so a visitor without
       JavaScript never renders a page to put one on. The consent default must sit ahead of both. */
    this.gtag('consent', 'default', this.cookieConsentService.consentModeState());
    this.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
    /* Consent Mode only reaches Google's tags. These are the events Consent Studio pushes on the portal,
       so a Meta or TikTok tag triggered on them there fires on the same consent here. */
    this.cookieConsentService
      .grantedCategories()
      .forEach((category) => this.dataLayer.push({ event: `cookie_consent_granted_${category}` }));

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`;
    document.head.appendChild(script);
  }

  /* GTM runs a dataLayer entry as a gtag command only when it is an `arguments` object, never an array. */
  private gtag(..._args: unknown[]): void {
    this.dataLayer.push(arguments);
  }
}
